import React, { useState, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from 'recharts'
import { Clock, Sunrise, Minus } from 'lucide-react'

const BUCKETS = [180, 90, 60, 30, 21, 14, 7, 3, 1] as const
type BucketKey = (typeof BUCKETS)[number]

const BUCKET_LABELS: Record<BucketKey, string> = {
  180: '180d',
  90: '90d',
  60: '60d',
  30: '30d',
  21: '21d',
  14: '14d',
  7: '7d',
  3: '3d',
  1: '1d',
}

type ModifierMap = Record<BucketKey, number>

const PRESETS: Record<string, { label: string; icon: typeof Clock; values: ModifierMap }> = {
  last_minute: {
    label: 'Last-minute premium',
    icon: Clock,
    values: { 180: -5, 90: -3, 60: 0, 30: 0, 21: 0, 14: 5, 7: 8, 3: 12, 1: 15 },
  },
  early_bird: {
    label: 'Early-bird discount',
    icon: Sunrise,
    values: { 180: 10, 90: 8, 60: 5, 30: 0, 21: -3, 14: -5, 7: -8, 3: -12, 1: -15 },
  },
  flat: {
    label: 'Flat',
    icon: Minus,
    values: { 180: 0, 90: 0, 60: 0, 30: 0, 21: 0, 14: 0, 7: 0, 3: 0, 1: 0 },
  },
}

const BASE_RATE = 100

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] text-muted-foreground">
        {d.label} before check-in
      </p>
      <p className="text-sm font-semibold text-foreground tabular-nums">
        {d.modifier >= 0 ? '+' : ''}{d.modifier}%
      </p>
      <p className="text-[11px] text-muted-foreground tabular-nums">
        £{(BASE_RATE * (1 + d.modifier / 100)).toFixed(0)}
      </p>
    </div>
  )
}

export default function LeadTimeCurveBuilder() {
  const [modifiers, setModifiers] = useState<ModifierMap>({ ...PRESETS.last_minute.values })

  const updateModifier = useCallback((bucket: BucketKey, value: number) => {
    setModifiers((prev) => ({ ...prev, [bucket]: value }))
  }, [])

  const applyPreset = useCallback((key: string) => {
    setModifiers({ ...PRESETS[key].values })
  }, [])

  // Chart data — reversed so 180d is on the left, 1d is on the right
  const chartData = useMemo(() => {
    return BUCKETS.map((bucket) => ({
      days: bucket,
      label: BUCKET_LABELS[bucket],
      modifier: modifiers[bucket],
      positiveModifier: modifiers[bucket] > 0 ? modifiers[bucket] : 0,
      negativeModifier: modifiers[bucket] < 0 ? modifiers[bucket] : 0,
    }))
  }, [modifiers])

  return (
    <div className="space-y-5">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(PRESETS).map(([key, preset]) => {
          const Icon = preset.icon
          const isActive = BUCKETS.every((b) => modifiers[b] === preset.values[b])
          return (
            <Button
              key={key}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset(key)}
              className={cn(
                'rounded-lg text-xs gap-1.5',
                isActive && 'shadow-sm'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {preset.label}
            </Button>
          )
        })}
      </div>

      {/* Chart */}
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id="leadPositive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#0d9488" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="leadNegative" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#e11d48" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#e11d48" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
              width={45}
            />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
            <RechartsTooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="positiveModifier"
              stroke="#0d9488"
              strokeWidth={2}
              fill="url(#leadPositive)"
              animationDuration={400}
              animationEasing="ease-out"
            />
            <Area
              type="monotone"
              dataKey="negativeModifier"
              stroke="#e11d48"
              strokeWidth={2}
              fill="url(#leadNegative)"
              animationDuration={400}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Axis context labels */}
      <div className="flex justify-between px-4 -mt-2">
        <span className="text-[10px] text-muted-foreground">Far out</span>
        <span className="text-[10px] text-muted-foreground">Check-in</span>
      </div>

      {/* Modifier inputs grid */}
      <div className="grid grid-cols-9 gap-1">
        {BUCKETS.map((bucket) => (
          <div key={bucket} className="flex flex-col items-center gap-1">
            <span className="text-[10px] text-muted-foreground font-medium">
              {BUCKET_LABELS[bucket]}
            </span>
            <div className="relative">
              <input
                type="number"
                value={modifiers[bucket]}
                onChange={(e) => {
                  const val = e.target.value === '' || e.target.value === '-' ? 0 : Number(e.target.value)
                  updateModifier(bucket, Math.max(-50, Math.min(50, val)))
                }}
                className={cn(
                  'w-full h-8 rounded-md border text-center text-xs tabular-nums font-medium transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary',
                  modifiers[bucket] > 0
                    ? 'border-emerald-200 bg-emerald-50/50 text-emerald-700'
                    : modifiers[bucket] < 0
                      ? 'border-rose-200 bg-rose-50/50 text-rose-700'
                      : 'border-slate-200 bg-white text-slate-500'
                )}
              />
              <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                %
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Rate preview row */}
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">
          Rate preview (£{BASE_RATE} base)
        </span>
        <div className="grid grid-cols-9 gap-1">
          {BUCKETS.map((bucket) => {
            const mod = modifiers[bucket]
            const rate = Math.round(BASE_RATE * (1 + mod / 100))
            return (
              <div
                key={bucket}
                className={cn(
                  'rounded-md py-1.5 text-center text-xs tabular-nums font-semibold transition-colors',
                  mod > 0
                    ? 'bg-emerald-50 text-emerald-700'
                    : mod < 0
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-slate-50 text-slate-500'
                )}
              >
                £{rate}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
